import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { useLanguage } from '../context/LanguageContext';

// Styled components
const SelectorContainer = styled.div`
  position: relative;
  margin-left: 1.5rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    margin-left: 1rem;
  }
`;

const LanguageButton = styled.button.attrs(props => ({
  style: {
    color: props.isScrolled ? props.theme.colors.text : 'white'
  }
}))`
  display: flex;
  align-items: center;
  background: transparent;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.3s ease;
  border-radius: 5px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  span {
    margin-right: 0.25rem;
  }
  
  svg {
    transition: transform 0.3s ease;
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
    margin-left: 0.25rem;
  }
`;

const DropdownMenu = styled.div.attrs(props => ({
  style: {
    opacity: props.isOpen ? 1 : 0,
    visibility: props.isOpen ? 'visible' : 'hidden',
    transform: props.isOpen ? 'translateY(0)' : 'translateY(-10px)'
  }
}))`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadows.medium};
  overflow: hidden;
  min-width: 150px;
  z-index: 1000;
  transition: opacity 0.3s ease, transform 0.3s ease, visibility 0.3s ease;
`;

const LanguageOption = styled.button.attrs(props => ({
  style: {
    background: props.isActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
    color: props.isActive ? props.theme.colors.primary : props.theme.colors.text,
    fontWeight: props.isActive ? '600' : '400'
  },
  'data-active': props.isActive ? 'true' : undefined
}))`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
  
  span {
    margin-right: 0.75rem;
    font-size: 1.25rem;
  }
`;

const DropdownOverlay = styled.div.attrs(props => ({
  style: {
    display: props.isOpen ? 'block' : 'none'
  }
}))`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 999;
`;

// Language flags and names
const LANGUAGE_FLAGS = {
  en: '🇬🇧',
  de: '🇩🇪',
  fr: '🇫🇷'
};

const LANGUAGE_NAMES = {
  en: 'English',
  de: 'Deutsch',
  fr: 'Français'
};

// LanguageSelector component
const LanguageSelector = ({ isScrolled = false }) => {
  const { language, setLanguage, translate, languages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  
  // Toggle dropdown
  const toggleDropdown = useCallback(() => {
    setIsOpen(prevState => !prevState);
  }, []);
  
  // Handle language change
  const handleLanguageChange = useCallback((lang) => {
    setLanguage(lang);
    setIsOpen(false);
  }, [setLanguage]);
  
  // Get flag for current language
  const currentFlag = LANGUAGE_FLAGS[language] || '🌐';
  const currentName = LANGUAGE_NAMES[language] || language;
  
  return (
    <SelectorContainer ref={dropdownRef}>
      <LanguageButton 
        onClick={toggleDropdown} 
        isOpen={isOpen}
        isScrolled={isScrolled}
        aria-label={translate('lang.select', 'Select language')}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <span>{currentFlag}</span>
        <span className="hide-on-mobile">{currentName}</span>
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </LanguageButton>
      
      <DropdownOverlay isOpen={isOpen} onClick={() => setIsOpen(false)} />
      
      <DropdownMenu isOpen={isOpen}>
        {languages.map(lang => (
          <LanguageOption
            key={lang}
            isActive={language === lang}
            onClick={() => handleLanguageChange(lang)}
            aria-selected={language === lang}
          >
            <span>{LANGUAGE_FLAGS[lang]}</span>
            {LANGUAGE_NAMES[lang]}
          </LanguageOption>
        ))}
      </DropdownMenu>
    </SelectorContainer>
  );
};

export default LanguageSelector;